/**
 * /dashboard/people/new — add an employee (Phase 3c — 2026-06-01).
 *
 * RBAC: POST /api/v1/employees is RequireRole(company_admin, hr_manager)
 * + CSRF + Audit server-side. UNLIKE the soft "read-only" config pages
 * (leave types / onboarding templates), this is a HARD gate — a
 * `manager` (who CAN see /dashboard/people) or any other role must NOT
 * see the form at all, only a clean "no access" state, mirroring the
 * backend so we never render controls that just 403. The server is
 * still the boundary; this is the UX mirror.
 *
 * When the server can send email, every create path emails the joiner a
 * 7-day set-password invite (or an "added to company" note if they already
 * have a WorkRight login). When it can't (GET /employees/invite-delivery),
 * the copy says so: the admin shares a temporary password instead, and
 * "Reset temporary password" on the employee page hands out a new one.
 * "Add one" shows the per-person invite outcome the server reports.
 *
 * Migration note (2026-06-01, Next.js → Vite): port of
 * frontend/src/app/(dashboard)/dashboard/people/new/page.tsx. Swaps:
 *   - "use client" dropped
 *   - next/link → @tanstack/react-router Link (typed)
 */

import { createFileRoute, Link , useParams} from "@tanstack/react-router";
import { ArrowLeft, ShieldX, UserPlus, Users } from "lucide-react";
import { useAuth } from "@/lib/auth";
import type { Role } from "@/types/auth";
import { PageHeader } from "@/components/shared/page-header";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { AddOneForm } from "@/components/people/add-one-form";
import { ImportCsv } from "@/components/people/import-csv";
import { SeatLimitNotice, SeatMeter } from "@/components/people/seat-meter";
import { useInviteDelivery } from "@/hooks/use-employee-access";

// Mirrors the backend hrAdminRole = RequireRole(company_admin,
// hr_manager) — the exact RBAC on POST /api/v1/employees.
const CAN_ADD_PEOPLE = new Set<Role>(["company_admin", "hr_manager"]);

export const Route = createFileRoute("/_authgated/app/$tenantSlug/people/new")({
  component: AddPeopleRoute,
});

function AddPeopleRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const { user } = useAuth();
  const role = user?.role;
  const allowed = !!role && CAN_ADD_PEOPLE.has(role);
  const delivery = useInviteDelivery(allowed);
  const emailConfigured = delivery.data?.email_configured;
  const validDays = delivery.data?.invite_valid_days ?? 7;

  if (!allowed) {
    // Hard gate: no form, just a clean explanation + a way back. The
    // server would 403 every POST anyway; this is the honest UX mirror.
    return (
      <>
        <PageHeader
          title="Add People"
          description="Bring your team into WorkRight."
        />
        <Card>
          <CardContent className="flex flex-col items-center gap-4 py-16 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-accent text-accent-foreground">
              <ShieldX className="h-6 w-6" />
            </span>
            <div className="space-y-1">
              <p className="text-base font-medium text-gray-900">
                You don&apos;t have access
              </p>
              <p className="max-w-md text-sm text-muted-foreground">
                Only company admins and HR managers can add people. Ask
                an admin on your team to invite new joiners, or head back
                to the directory.
              </p>
            </div>
            <Button asChild variant="outline" className="gap-1.5">
              <Link to="/app/$tenantSlug/people" params={{ tenantSlug }}>
                <ArrowLeft className="h-4 w-4" />
                Back to People
              </Link>
            </Button>
          </CardContent>
        </Card>
      </>
    );
  }

  // Top-of-page Back button removed per Audit 4 Quick-Win #4 — the
  // bottom Cancel button on AddOneForm handles it for the "Add one"
  // tab; the "Import CSV" tab is self-contained (upload → preview →
  // commit) and exiting it just navigates away normally.
  return (
    <>
      <PageHeader
        title="Add People"
        description="Create accounts for your team."
      />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Users className="h-5 w-5 text-primary" />
            Invite Your Team
          </CardTitle>
          <CardDescription>
            Add one person at a time, or import a CSV of up to 5,000 rows.{" "}
            {emailConfigured === false
              ? "Email isn't set up for your company yet, so nobody is emailed: give each person their temporary password yourself (“Reset temporary password” on their profile makes a new one) and they choose their own at first sign-in."
              : emailConfigured
                ? `We email each person a link to set their own password (valid ${validDays} days). If an invite goes missing, resend it from their profile.`
                : null}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="one">
            <TabsList className="mb-6">
              <TabsTrigger value="one" className="gap-1.5">
                <UserPlus className="h-4 w-4" />
                Add One
              </TabsTrigger>
              <TabsTrigger value="csv" className="gap-1.5">
                <Users className="h-4 w-4" />
                Import CSV
              </TabsTrigger>
            </TabsList>
            <TabsContent value="one" className="space-y-5">
              {/* Seat check BEFORE the form is filled (people QA #8). */}
              <SeatMeter />
              <SeatLimitNotice />
              <AddOneForm />
            </TabsContent>
            <TabsContent value="csv">
              <ImportCsv />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </>
  );
}
